import { ArrowLeft, ShieldCheck, Mail, Database, Server, Cookie, User, Lock, AlertTriangle, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * Comprehensive privacy policy tailored for Apple App Store & Google Play
 * submission. Covers RGPD/GDPR requirements: identity of the controller,
 * data collected, purposes, legal basis, retention, sharing, rights, and
 * how to contact the DPO.
 */

const CONTACT_EMAIL = 'supportclaypulse@gmail.com';
const LAST_UPDATED = 'Juillet 2026';

const DATA_ROWS = [
  { cat: "Compte", items: "Email, mot de passe chiffré (bcrypt), nom (optionnel)" },
  { cat: "Sessions de tir", items: "Date, discipline, station, plateaux tirés / touchés, notes, météo (optionnel)" },
  { cat: "Matériel", items: "Fusil(s), chokes, cartouches, stock" },
  { cat: "Identifiants techniques", items: "device_id (identifiant local de l'appareil), user-agent, adresse IP (temporaire, pour la lutte anti-fraude)" },
  { cat: "Paiement", items: "Aucune donnée bancaire n'est stockée par ClayPulse. Toutes les transactions passent par Stripe (PCI-DSS)." },
  { cat: "Support", items: "Contenu des emails que tu nous envoies, uniquement pour te répondre" },
];

const THIRD_PARTIES = [
  { name: "Stripe (États-Unis, DPF certifié)", purpose: "Traitement sécurisé des paiements et gestion des abonnements. Aucune donnée de carte n'est vue par ClayPulse.", link: "https://stripe.com/privacy" },
  { name: "Google (Gemini AI, Union Européenne)", purpose: "Traitement des questions envoyées à Clayo, notre coach IA. Aucune donnée personnelle identifiable n'est envoyée (uniquement le texte de la question).", link: "https://policies.google.com/privacy" },
  { name: "Resend (Union Européenne)", purpose: "Envoi des emails transactionnels (confirmation, mot de passe oublié).", link: "https://resend.com/legal/privacy-policy" },
  { name: "MongoDB Atlas (Union Européenne)", purpose: "Hébergement de la base de données (serveurs en Europe).", link: "https://www.mongodb.com/legal/privacy-policy" },
];

const RGPD_RIGHTS = [
  { title: "Droit d'accès", desc: "Obtenir une copie de toutes tes données personnelles que nous détenons." },
  { title: "Droit de rectification", desc: "Corriger les données inexactes ou incomplètes te concernant." },
  { title: "Droit à l'effacement (droit à l'oubli)", desc: "Demander la suppression de ton compte et de toutes tes données." },
  { title: "Droit à la portabilité", desc: "Récupérer tes données au format JSON structuré (exportable depuis l'app)." },
  { title: "Droit d'opposition", desc: "T'opposer au traitement de tes données pour des motifs légitimes." },
  { title: "Droit à la limitation", desc: "Demander la suspension temporaire du traitement de tes données." },
];

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#0D0D0D] text-white">
      <div className="max-w-3xl mx-auto px-6 py-16 sm:py-20">
        <Link
          to="/"
          data-testid="privacy-back-link"
          className="inline-flex items-center gap-2 text-[#A0A0A0] hover:text-[#FF6B35] text-sm mb-8 transition-colors"
        >
          <ArrowLeft size={16} />
          Retour à l&apos;accueil
        </Link>

        <header className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FF6B35]/10 border border-[#FF6B35]/30 mb-4">
            <ShieldCheck size={13} className="text-[#FF6B35]" />
            <span className="text-[#FF6B35] text-[11px] font-bold uppercase tracking-[0.2em]">
              RGPD · Vie privée
            </span>
          </div>
          <h1 className="font-['Poppins'] font-black text-3xl sm:text-4xl mb-3">
            Politique de confidentialité
          </h1>
          <p className="text-[#A0A0A0] text-sm">
            Dernière mise à jour : <strong className="text-white">{LAST_UPDATED}</strong> ·
            Version 2.0
          </p>
        </header>

        <div className="space-y-10 text-[#C8C8C8] text-sm sm:text-[15px] leading-relaxed">
          {/* Preamble */}
          <section>
            <p className="p-4 rounded-xl bg-[#FF6B35]/8 border border-[#FF6B35]/20 text-white/90">
              Ta vie privée est essentielle. Cette politique explique clairement quelles données
              ClayPulse collecte, pourquoi, comment elles sont protégées, et comment tu peux les
              contrôler à tout moment. Elle est conforme au Règlement Général sur la Protection
              des Données (RGPD, UE 2016/679) et à la loi française Informatique &amp; Libertés.
            </p>
          </section>

          {/* 1. Controller identity */}
          <section>
            <h2 className="text-white font-['Poppins'] font-bold text-lg mb-3 flex items-center gap-2">
              <User size={17} className="text-[#FF6B35]" /> 1. Responsable du traitement
            </h2>
            <p>
              L&apos;éditeur et responsable du traitement des données est <strong className="text-white">ClayPulse</strong>.
              Toute demande relative à cette politique peut être adressée par email à{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#FF6B35] hover:underline">
                {CONTACT_EMAIL}
              </a>.
            </p>
          </section>

          {/* 2. Data collected */}
          <section>
            <h2 className="text-white font-['Poppins'] font-bold text-lg mb-3 flex items-center gap-2">
              <Database size={17} className="text-[#FF6B35]" /> 2. Données collectées
            </h2>
            <p className="mb-4">
              ClayPulse ne collecte <strong className="text-white">que</strong> les données
              nécessaires au fonctionnement du service. Aucune donnée n&apos;est vendue,
              louée ou partagée à des fins publicitaires.
            </p>
            <div className="overflow-hidden rounded-xl border border-white/10">
              <table className="w-full text-xs sm:text-sm">
                <thead className="bg-white/[0.04]">
                  <tr>
                    <th className="text-left p-3 font-semibold text-white w-1/3">Catégorie</th>
                    <th className="text-left p-3 font-semibold text-white">Détail</th>
                  </tr>
                </thead>
                <tbody>
                  {DATA_ROWS.map((r, i) => (
                    <tr key={i} className="border-t border-white/5">
                      <td className="p-3 text-white/90 font-medium align-top">{r.cat}</td>
                      <td className="p-3 text-[#B5B5B5] align-top">{r.items}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* 3. Purposes & legal basis */}
          <section>
            <h2 className="text-white font-['Poppins'] font-bold text-lg mb-3 flex items-center gap-2">
              <FileText size={17} className="text-[#FF6B35]" /> 3. Finalités et bases légales
            </h2>
            <ul className="space-y-2 list-disc list-inside pl-1">
              <li>
                <strong className="text-white">Fournir le service</strong> — création de compte,
                enregistrement des sessions, statistiques (base légale : exécution du contrat).
              </li>
              <li>
                <strong className="text-white">Traiter les paiements et abonnements</strong> —
                via Stripe (base légale : exécution du contrat).
              </li>
              <li>
                <strong className="text-white">Assurer la sécurité</strong> — logs, lutte
                anti-fraude, prévention des abus (base légale : intérêt légitime).
              </li>
              <li>
                <strong className="text-white">Communications transactionnelles</strong> — email
                de bienvenue, réinitialisation de mot de passe (base légale : exécution du contrat).
              </li>
              <li>
                <strong className="text-white">Support et amélioration continue</strong> —
                traitement des demandes envoyées par email (base légale : intérêt légitime).
              </li>
            </ul>
          </section>

          {/* 4. Retention */}
          <section>
            <h2 className="text-white font-['Poppins'] font-bold text-lg mb-3 flex items-center gap-2">
              <Server size={17} className="text-[#FF6B35]" /> 4. Durée de conservation
            </h2>
            <ul className="space-y-2 list-disc list-inside pl-1">
              <li>
                <strong className="text-white">Compte actif</strong> — tant que le compte est
                utilisé.
              </li>
              <li>
                <strong className="text-white">Compte inactif</strong> — 24 mois après la
                dernière connexion, un email de rappel est envoyé. Sans réponse sous 30 jours,
                le compte est supprimé.
              </li>
              <li>
                <strong className="text-white">Suppression de compte</strong> — toutes les
                données sont supprimées définitivement sous 48 h.
              </li>
              <li>
                <strong className="text-white">Sessions invitées</strong> — supprimées
                automatiquement après 24 heures.
              </li>
              <li>
                <strong className="text-white">Données comptables</strong> (factures Stripe) —
                conservées 10 ans conformément aux obligations légales françaises.
              </li>
              <li>
                <strong className="text-white">Logs de sécurité</strong> — 90 jours maximum.
              </li>
            </ul>
          </section>

          {/* 5. Sharing */}
          <section>
            <h2 className="text-white font-['Poppins'] font-bold text-lg mb-3 flex items-center gap-2">
              <Lock size={17} className="text-[#FF6B35]" /> 5. Sous-traitants et partage
            </h2>
            <p className="mb-4">
              ClayPulse fait appel à un nombre limité de sous-traitants techniques (hébergement,
              paiements, envoi d&apos;emails), tous certifiés RGPD :
            </p>
            <div className="space-y-3">
              {THIRD_PARTIES.map((tp, i) => (
                <div key={i} className="p-4 rounded-xl bg-white/[0.03] border border-white/5">
                  <div className="flex items-center justify-between gap-3 mb-1.5 flex-wrap">
                    <span className="text-white font-semibold text-sm">{tp.name}</span>
                    <a
                      href={tp.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#FF6B35] hover:underline text-[11px]"
                    >
                      Politique de confidentialité →
                    </a>
                  </div>
                  <p className="text-[#B5B5B5] text-xs sm:text-sm leading-relaxed">
                    {tp.purpose}
                  </p>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-[#888]">
              <strong className="text-white">Aucun autre partage</strong> avec des tiers
              publicitaires, courtiers en données, ou réseaux sociaux.
            </p>
          </section>

          {/* 6. Security */}
          <section>
            <h2 className="text-white font-['Poppins'] font-bold text-lg mb-3 flex items-center gap-2">
              <ShieldCheck size={17} className="text-[#FF6B35]" /> 6. Sécurité
            </h2>
            <ul className="space-y-2 list-disc list-inside pl-1">
              <li>Chiffrement des mots de passe (bcrypt avec salt).</li>
              <li>Chiffrement HTTPS (TLS 1.3) sur toutes les communications.</li>
              <li>Serveurs hébergés en Europe (Union Européenne).</li>
              <li>Accès à la base de données restreint et journalisé.</li>
              <li>Sauvegardes automatiques quotidiennes chiffrées.</li>
              <li>Tokens de session courts (JWT expirant après 7 jours).</li>
            </ul>
          </section>

          {/* 7. Rights */}
          <section>
            <h2 className="text-white font-['Poppins'] font-bold text-lg mb-3 flex items-center gap-2">
              <User size={17} className="text-[#FF6B35]" /> 7. Tes droits RGPD
            </h2>
            <p className="mb-4">
              Conformément au RGPD, tu disposes des droits suivants sur tes données
              personnelles :
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {RGPD_RIGHTS.map((r, i) => (
                <div key={i} className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                  <div className="text-white font-semibold text-sm mb-1">{r.title}</div>
                  <div className="text-[#A0A0A0] text-xs leading-relaxed">{r.desc}</div>
                </div>
              ))}
            </div>
            <p className="mt-4 text-sm">
              Pour exercer un droit, écris-nous à{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#FF6B35] hover:underline">
                {CONTACT_EMAIL}
              </a>{' '}
              — nous répondons sous 30 jours maximum (délai légal). Une réponse dans les 48 h
              est habituelle.
            </p>
            <p className="mt-3 text-xs text-[#888]">
              En cas de désaccord, tu peux introduire une réclamation auprès de la CNIL
              (Commission Nationale de l&apos;Informatique et des Libertés) :{' '}
              <a
                href="https://www.cnil.fr/fr/plaintes"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#FF6B35] hover:underline"
              >
                cnil.fr/fr/plaintes
              </a>.
            </p>
          </section>

          {/* 8. Cookies */}
          <section>
            <h2 className="text-white font-['Poppins'] font-bold text-lg mb-3 flex items-center gap-2">
              <Cookie size={17} className="text-[#FF6B35]" /> 8. Cookies et stockage local
            </h2>
            <p className="mb-3">
              Le site web ClayPulse et l&apos;application utilisent uniquement des cookies et
              données locales <strong className="text-white">strictement nécessaires</strong> au
              fonctionnement :
            </p>
            <ul className="space-y-1.5 list-disc list-inside pl-1 text-sm">
              <li><strong className="text-white">Session</strong> : maintenir ta connexion (token JWT).</li>
              <li><strong className="text-white">device_id</strong> : identifier ton appareil pour synchroniser tes données.</li>
              <li><strong className="text-white">Préférences</strong> : thème, langue, filtres actifs.</li>
            </ul>
            <p className="mt-3 text-xs text-[#888]">
              Aucun cookie publicitaire, aucun cookie de traçage tiers, aucun outil d&apos;analyse
              comportementale (pas de Google Analytics, Facebook Pixel, etc.).
            </p>
          </section>

          {/* 9. Children */}
          <section>
            <h2 className="text-white font-['Poppins'] font-bold text-lg mb-3 flex items-center gap-2">
              <AlertTriangle size={17} className="text-[#FF6B35]" /> 9. Mineurs
            </h2>
            <p>
              ClayPulse n&apos;est pas destiné aux enfants de moins de 13 ans. Nous ne
              collectons pas sciemment de données concernant des mineurs. Si tu es un parent
              ou tuteur et que tu constates qu&apos;un mineur a créé un compte, contacte-nous
              pour la suppression immédiate.
            </p>
          </section>

          {/* 10. International transfers */}
          <section>
            <h2 className="text-white font-['Poppins'] font-bold text-lg mb-3 flex items-center gap-2">
              <Server size={17} className="text-[#FF6B35]" /> 10. Transferts internationaux
            </h2>
            <p>
              Tes données sont hébergées en Union Européenne. Seul Stripe (aux États-Unis)
              peut recevoir certaines données de paiement — il est certifié
              <em> Data Privacy Framework (DPF)</em>, cadre légal validé par la Commission
              Européenne pour les transferts UE → US.
            </p>
          </section>

          {/* 11. Updates */}
          <section>
            <h2 className="text-white font-['Poppins'] font-bold text-lg mb-3 flex items-center gap-2">
              <FileText size={17} className="text-[#FF6B35]" /> 11. Modifications
            </h2>
            <p>
              Cette politique peut être mise à jour pour refléter des évolutions techniques
              ou légales. Toute modification substantielle est notifiée par email au moins
              15 jours avant son entrée en vigueur. La date en haut de cette page indique
              toujours la dernière version.
            </p>
          </section>

          {/* 12. Contact */}
          <section
            data-testid="privacy-contact"
            className="p-6 rounded-2xl bg-gradient-to-br from-[#FF6B35]/8 to-transparent border border-[#FF6B35]/20"
          >
            <h2 className="text-white font-['Poppins'] font-bold text-lg mb-3 flex items-center gap-2">
              <Mail size={17} className="text-[#FF6B35]" /> 12. Contact
            </h2>
            <p className="mb-4">
              Pour toute question relative à la protection de tes données personnelles ou pour
              exercer tes droits, contacte-nous :
            </p>
            <a
              href={`mailto:${CONTACT_EMAIL}?subject=Politique%20de%20confidentialit%C3%A9`}
              data-testid="privacy-contact-email"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FF6B35] text-white font-semibold text-sm hover:bg-[#F76B1C] hover:shadow-[0_0_20px_rgba(255,107,53,0.4)] transition-all"
            >
              <Mail size={15} />
              {CONTACT_EMAIL}
            </a>
            <p className="mt-4 text-xs text-[#888]">
              Réponse habituelle sous 24 à 48 h ouvrés — délai légal maximum : 30 jours.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
